// src/pages/org/OrgDocsTable.jsx
import React, { useState, useMemo, useEffect } from "react";
import { issueDocumentTypeLabel } from "lib/issueDocuments";

export default function OrgDocsTable({
  docs = [],
  isAdmin = false,
  handleOpen,
  handleDelete,
  selectedOrg,   // ✅ get org selection from parent
}) {
  const [filterParty, setFilterParty] = useState("");
  const [filterTrade, setFilterTrade] = useState("");
  const [filterIssue, setFilterIssue] = useState("");
  const [filterType, setFilterType] = useState("");

  // ✅ Reset all filters when org changes
  useEffect(() => {
    setFilterParty("");
    setFilterTrade("");
    setFilterIssue("");
    setFilterType("");
  }, [selectedOrg]);

  const partyOptions = useMemo(
    () =>
      [
        ...new Set(
          docs.flatMap((d) =>
            (d.scopes || []).map((s) => s.party?.canon_name).filter(Boolean)
          )
        ),
      ],
    [docs]
  );

  const tradeOptions = useMemo(
    () =>
      [
        ...new Set(
          docs.flatMap((d) =>
            (d.scopes || []).map((s) => s.trade?.trade_id).filter(Boolean)
          )
        ),
      ],
    [docs]
  );

  const typeOptions = useMemo(
    () => [...new Set(docs.map((d) => d.doc_type).filter(Boolean))],
    [docs]
  );

  const issueOptions = useMemo(
    () => [
      ...new Set(
        docs.flatMap((d) =>
          (d.scopes || []).map((s) => s.issue?.name).filter(Boolean)
        )
      ),
    ],
    [docs]
  );

  const filteredDocs = useMemo(
    () =>
      docs.filter((d) => {
        const parties = (d.scopes || []).map((s) => s.party?.canon_name);
        const trades = (d.scopes || []).map((s) => s.trade?.trade_id);
        const issues = (d.scopes || []).map((s) => s.issue?.name);
        return (
          (!filterParty || parties.includes(filterParty)) &&
          (!filterTrade || trades.includes(filterTrade)) &&
          (!filterIssue || issues.includes(filterIssue)) &&
          (!filterType || d.doc_type === filterType)
        );
      }),
    [docs, filterParty, filterTrade, filterIssue, filterType]
  );

  const sortedDocs = useMemo(() => {
    return [...filteredDocs].sort((a, b) => {
      const aOrg = a.org?.name || "";
      const bOrg = b.org?.name || "";
      if (aOrg !== bOrg) return aOrg.localeCompare(bOrg);

      const aParty = (a.scopes?.[0]?.party?.canon_name || "").toLowerCase();
      const bParty = (b.scopes?.[0]?.party?.canon_name || "").toLowerCase();
      if (aParty !== bParty) return aParty.localeCompare(bParty);

      const aTrade = (a.scopes?.[0]?.trade?.trade_id || "").toLowerCase();
      const bTrade = (b.scopes?.[0]?.trade?.trade_id || "").toLowerCase();
      return aTrade.localeCompare(bTrade);
    });
  }, [filteredDocs]);

  return (
    <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
      <div className="overflow-x-auto pb-4">
        <table className="w-full text-sm text-foreground">
          <thead>
            <tr className="bg-muted/50">
              {/* Party filter */}
              <th className="p-2 text-left">
                <div className="relative">
                  <select
                    value={filterParty}
                    onChange={(e) => setFilterParty(e.target.value)}
                    className="appearance-none pr-9 pl-3 h-10 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                               focus:outline-none focus:ring-2 focus:ring-ring w-full"
                  >
                    <option value="">All Parties</option>
                    {partyOptions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <svg
                    aria-hidden="true"
                    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.2l3.71-2.97a.75.75 0 1 1 .94 1.17l-4.18 3.35a.75.75 0 0 1-.94 0L5.25 8.4a.75.75 0 0 1-.02-1.19z"/>
                  </svg>
                </div>
              </th>

              {/* Trade filter */}
              <th className="p-2 text-left">
                <div className="relative">
                  <select
                    value={filterTrade}
                    onChange={(e) => setFilterTrade(e.target.value)}
                    className="appearance-none pr-9 pl-3 h-10 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                               focus:outline-none focus:ring-2 focus:ring-ring w-full"
                  >
                    <option value="">All Trades</option>
                    {tradeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <svg
                    aria-hidden="true"
                    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.2l3.71-2.97a.75.75 0 1 1 .94 1.17l-4.18 3.35a.75.75 0 0 1-.94 0L5.25 8.4a.75.75 0 0 1-.02-1.19z"/>
                  </svg>
                </div>
              </th>

              {/* Issue filter */}
              <th className="p-2 text-left">
                <select
                  value={filterIssue}
                  onChange={(e) => setFilterIssue(e.target.value)}
                  className="input-app h-10 w-full"
                >
                  <option value="">All Issues</option>
                  {issueOptions.map((issue) => <option key={issue} value={issue}>{issue}</option>)}
                </select>
              </th>

              {/* Type filter */}
              <th className="p-2 text-left">
                <div className="relative">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="appearance-none pr-9 pl-3 h-10 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                               focus:outline-none focus:ring-2 focus:ring-ring w-full"
                  >
                    <option value="">All Types</option>
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>
                        {issueDocumentTypeLabel(t)}
                      </option>
                    ))}
                  </select>
                  <svg
                    aria-hidden="true"
                    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.2l3.71-2.97a.75.75 0 1 1 .94 1.17l-4.18 3.35a.75.75 0 0 1-.94 0L5.25 8.4a.75.75 0 0 1-.02-1.19z"/>
                  </svg>
                </div>
              </th>

              <th className="p-2 text-left">Title</th>
              <th className="p-2 text-left">File</th>
            </tr>
          </thead>

          <tbody>
            {sortedDocs.map((doc) => (
              <tr key={doc.id} className="border-t border-border">
                <td className="p-2">
                  {(doc.scopes || [])
                    .map((s) => s.party?.canon_name)
                    .filter(Boolean)
                    .join(", ") || "—"}
                </td>
                <td className="p-2">
                  {(doc.scopes || [])
                    .map((s) => s.trade?.trade_id)
                    .filter(Boolean)
                    .join(", ") || "—"}
                </td>
                <td className="p-2">
                  {(doc.scopes || [])
                    .map((s) => s.issue?.name)
                    .filter(Boolean)
                    .join(", ") || "—"}
                </td>
                <td className="p-2">{issueDocumentTypeLabel(doc.doc_type)}</td>
                <td className="p-2">{doc.title}</td>
                <td className="p-2">
                  <button
                    onClick={() => handleOpen?.(doc.file_path)}
                    className="text-blue-600 underline hover:text-blue-800 mr-2"
                  >
                    Open
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete?.(doc.id, doc.file_path)}
                      className="text-red-600 underline hover:text-red-800"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sortedDocs.length === 0 && (
              <tr>
                <td colSpan={6} className="p-4 text-center text-muted">
                  No documents found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
